import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { SkeletonCard } from './SkeletonLoader';
import Icon from './Icon';
import '../styles/AnalyticsDashboard.css';

function AnalyticsDashboard() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [topBikes, setTopBikes] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [overviewRes, revenueRes, topBikesRes] = await Promise.all([
        adminAPI.getAnalyticsOverview(),
        adminAPI.getRevenueByMonth({ months: 12 }),
        adminAPI.getTopBikes({ limit: 10 }),
      ]);

      setOverview(overviewRes.data);
      setMonthlyRevenue(revenueRes.data || []);
      setTopBikes(topBikesRes.data || []);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="analytics-dashboard">
        <SkeletonCard />
      </div>
    );
  }

  // Calculate max revenue for chart scaling
  const maxRevenue = monthlyRevenue.length > 0
    ? Math.max(...monthlyRevenue.map(m => m.revenue || 0))
    : 1;

  return (
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <h2>Analytics Dashboard</h2>
        <p>Detailed statistics and trends</p>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div className="analytics-overview">
          <div className="stat-card">
            <div className="stat-icon">
              <Icon name="bike" color="primary" darkMode={true} size={32} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{overview.total_bikes || 0}</div>
              <div className="stat-label">Total Bikes</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Icon name="users" color="primary" darkMode={true} size={32} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{overview.total_users || 0}</div>
              <div className="stat-label">Felhasználók</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Icon name="service" color="primary" darkMode={true} size={32} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{overview.total_services || 0}</div>
              <div className="stat-label">Szervizek</div>
            </div>
          </div>
          <div className="stat-card stat-card-revenue">
            <div className="stat-icon">
              <Icon name="payment" color="primary" darkMode={true} size={32} />
            </div>
            <div className="stat-content">
              <div className="stat-value">
                {overview.total_revenue ? overview.total_revenue.toLocaleString('en-GB') : 0} £
              </div>
              <div className="stat-label">Total Revenue</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Icon name="success" color="success" darkMode={true} size={32} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{overview.active_users || 0}</div>
              <div className="stat-label">Active Users</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Icon name="calendar" color="primary" darkMode={true} size={32} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{overview.services_this_month || 0}</div>
              <div className="stat-label">Services This Month</div>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Revenue Chart */}
      <div className="analytics-card">
        <h3>Monthly Revenue Trend</h3>
        {monthlyRevenue.length === 0 ? (
          <p className="empty-state">No revenue data yet</p>
        ) : (
          <div className="chart-container">
            <div className="chart-bars">
              {monthlyRevenue.map((month, index) => {
                const height = maxRevenue > 0 ? (month.revenue / maxRevenue) * 100 : 0;
                return (
                  <div key={index} className="chart-bar-wrapper">
                    <div className="chart-bar" style={{ height: `${height}%` }}>
                      <div className="chart-bar-value">
                        {month.revenue.toLocaleString('en-GB')} £
                      </div>
                    </div>
                    <div className="chart-bar-label">
                      {new Date(month.month + '-01').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                    </div>
                    <div className="chart-bar-count">{month.service_count} szerviz</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Top Bikes */}
      <div className="analytics-card">
        <h3>Most Serviced Bikes</h3>
        {topBikes.length === 0 ? (
          <p className="empty-state">No data yet</p>
        ) : (
          <div className="top-bikes-list">
            {topBikes.map((bike, index) => (
              <div key={bike.bike_id} className="top-bike-item">
                <div className="top-bike-rank">#{index + 1}</div>
                <div className="top-bike-info">
                  <div className="top-bike-name">
                    {bike.brand} {bike.model}
                  </div>
                  <div className="top-bike-details">
                    Frame: {bike.vin} • {bike.service_count} szerviz • {bike.total_spent.toLocaleString('en-GB')} £
                  </div>
                </div>
                <div className="top-bike-stats">
                  <div className="stat-badge">{bike.service_count} szerviz</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AnalyticsDashboard;


